// What the Terms and the Privacy Policy need that only the owner can say, and
// the version of each document.
//
// DRAFT: the two documents (scripts/legal.ts) are a first draft for a lawyer's
// review, not legal advice. Nothing here was reviewed by counsel.
//
// The owner supplied every value below on 2026-10-06. A value that starts
// with "[" is treated as a placeholder still to be filled: the pages show it
// marked, `bun run build` prints a warning naming it, and
// test/legal.test.ts lists it. None is one today.

export const LEGAL = {
  /** The company that operates Pocket Studio. */
  operator: "Pocket Nexus, Inc.",
  operatorKind: "a Delaware C corporation",
  operatorCountry: "the United States",
  /** The address people write to about their account, a takedown or their data: a mailbox that sends and receives. */
  contactEmail: "support@pocket.nexus",
  /** Whose law governs the Terms and where a dispute is heard. */
  governingLaw: "the laws of the State of Delaware, without regard to its conflict of laws rules",
  venue: "the state and federal courts located in Delaware",
  /**
   * The day of both documents' current text, as their pages print it ("Last
   * updated"). Change it with any change of either text.
   */
  effectiveDate: "October 13, 2026",
  /**
   * Whether the event log is also copied to PostHog (the Worker's
   * POSTHOG_API_KEY and POSTHOG_HOST). The Privacy Policy says which. Set it
   * true in the same change that sets those secrets.
   */
  analyticsForwarding: false,
  /**
   * Whether the membership is sold by Stripe's merchant of record, under the
   * name Link (Managed Payments; the Worker's STRIPE_MANAGED_PAYMENTS). The
   * Terms and the Privacy Policy say who takes the payment and who adds the
   * tax. Set it true in the same change that sets that variable "true", and
   * give TERMS_VERSION and PRIVACY_VERSION that day's date: who the seller is
   * changes what both documents mean.
   */
  merchantOfRecord: true,
} as const;

/**
 * The version of each document. An account records the versions it agreed to.
 *
 * The room asks an account once to accept the Terms when TERMS_VERSION is not
 * the one it accepted. Change TERMS_VERSION only for a change the Terms call
 * significant: one that takes away a right, adds an obligation or a charge,
 * or otherwise works against users; announce it 30 days before it takes
 * effect. A clarification, a new feature, a change in users' favour, a price
 * or an offer keeps the version: change the text and LEGAL.effectiveDate.
 *
 * The Privacy Policy is not accepted: PRIVACY_VERSION is recorded with an
 * acceptance of the Terms and never asks by itself. A significant change to
 * it is announced before it takes effect.
 *
 * 2026-10-06 was the first version of both. 2026-10-07: a published game's
 * source is kept and given to the people who remix it. 2026-10-08:
 * memberships given through offers. 2026-10-09: packages Pocket Studio makes
 * from a game's builds. 2026-10-10 (Terms): the price in yen. 2026-10-11:
 * prices leave the Terms, changes are asked for only when significant, the
 * Privacy Policy is no longer accepted, and the review of 2026-10-07 (minors,
 * refunds, price notice, liability carve-outs, notice before closing).
 */
export const TERMS_VERSION = "2026-10-11";
export const PRIVACY_VERSION = "2026-10-11";

export const TERMS_PATH = "/terms/";
export const PRIVACY_PATH = "/privacy/";

/** The placeholders still unfilled, by name. */
export function unfilled(): string[] {
  return Object.entries(LEGAL).filter(([, value]) => typeof value === "string" && value.startsWith("[")).map(([name]) => name);
}

/** GET /api/agreement, and the answer to POST /api/agreement. */
export interface AgreementResponse {
  /** The current version of each document. */
  terms: string;
  privacy: string;
  /** What this account agreed to and when, or null for a guest and for an account that has not. */
  accepted: { terms: string; privacy: string; at: number } | null;
  /** True when there is nothing to ask: a guest, or an account whose agreement is to the current versions. */
  current: boolean;
}
